/** Onboarding page: explains the product + the treasury→recipients flow in plain language.
 *  This is the first thing a new user (or tester) should read. */
export function StartHere() {
  return (
    <>
      <h2>Start here</h2>

      <div className="card">
        <h3>What is Praxis?</h3>
        <p>
          Praxis is a <b>control panel for blockchain keys</b> (Cosmos). It lets a bot or operator key perform on-chain actions -
          send tokens, vote, stake - <b>only within the rules you set</b>, and writes every action to a tamper-evident log. Think
          “AWS IAM + audit trail”, but for the Cosmos interchain.
        </p>
        <p className="muted">
          The key never <i>holds</i> the funds (non-custodial): it can only act inside a permission the treasury grants on-chain.
        </p>
      </div>

      <div className="card">
        <h3>How a payout works - treasury A pays D and E</h3>
        <div className="flow">
          <div className="node">
            <div className="ic">🏦</div>
            <b>Treasury (wallet A)</b>
            <span>Holds the funds. Signs ONE on-chain grant. Its private key never touches Praxis.</span>
          </div>
          <div className="arrow">
            <span className="gate">Gate 1 · on-chain grant</span>
            <span className="ar">→</span>
          </div>
          <div className="node">
            <div className="ic">🔑</div>
            <b>Operator key</b>
            <span>Lives in AWS KMS. Acts only within the grant. Never holds the funds.</span>
          </div>
          <div className="arrow">
            <span className="gate">Gate 2 · Praxis policy</span>
            <span className="ar">→</span>
          </div>
          <div className="node">
            <div className="ic">📜</div>
            <b>Policy (your rules)</b>
            <span>Limits, allow-list, approvals. Stricter than the grant.</span>
          </div>
          <div className="arrow">
            <span className="gate">execute / deny</span>
            <span className="ar">→</span>
          </div>
          <div className="node">
            <div className="ic">📥</div>
            <b>Recipients D, E</b>
            <span>Where the money lands.</span>
          </div>
        </div>
        <p className="hint">
          Both gates must pass. <b>Gate 1</b> (the on-chain grant) is enforced by the blockchain itself - even if Praxis were
          hacked, the operator could not exceed it. <b>Gate 2</b> (your policy) is the flexible layer you manage here. Every
          decision - allowed or denied - is recorded in the <a href="#/audit">Audit</a> log and anchored on-chain.
        </p>
      </div>

      <div className="card">
        <h3>The 4 things you work with</h3>
        <table>
          <tbody>
            <tr>
              <td>
                <b>📜 Policy</b>
              </td>
              <td>The rulebook: who may do what, where, within which limits. You build it in <a href="#/policies">Policies</a> (templates + wizard).</td>
            </tr>
            <tr>
              <td>
                <b>📨 Intent</b>
              </td>
              <td>A request: “do action X”. Praxis checks the policy + the on-chain grant, then executes or denies with a reason. See <a href="#/intents">Intents</a>.</td>
            </tr>
            <tr>
              <td>
                <b>✋ Approval</b>
              </td>
              <td>For larger actions: a human signs with their wallet (Keplr) before it runs (m-of-n).</td>
            </tr>
            <tr>
              <td>
                <b>🔗 Audit</b>
              </td>
              <td>A hash-chained, on-chain-anchored log of everything. Tamper-evident. See <a href="#/audit">Audit</a>.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Try it in 3 steps (nothing breaks)</h3>
        <ol>
          <li>
            Go to <a href="#/policies">Policies</a> → click the <b>💸 Capped payouts</b> template → set a max amount and a recipient → <b>Create policy</b>.
          </li>
          <li>
            Go to <a href="#/intents">Intents</a> → submit a <b>bank.send</b> within the limit → watch it run (or be rejected if there’s no on-chain grant - that’s fine).
          </li>
          <li>
            Submit one <i>over</i> the limit → see <b>POLICY_DENIED</b>. Then open <a href="#/audit">Audit</a> and hit <b>verify</b>.
          </li>
        </ol>
        <p className="muted small">That round-trip - create a rule, try to break it, watch it get blocked, see it logged - is Praxis in a nutshell.</p>
      </div>
    </>
  );
}
